import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Briefcase,
  ArrowLeft,
  Rocket,
  AlertTriangle,
  Target,
  KanbanSquare,
  Users,
  ClipboardList,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { B2B_PLANOS, B2B_ETAPAS, B2B_STAGES, B2B_DURACAO } from "@/lib/b2b/config";

export const Route = createFileRoute("/admin/plano-b2b")({
  head: () => ({
    meta: [
      { title: "Admin — Plano · B2B Clínicas" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-b2b">
      <PlanoB2bPage />
    </AdminGate>
  ),
});

function PlanoB2bPage() {
  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-4xl space-y-8 px-4 py-12 md:py-16">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> voltar ao centro de controle
        </Link>

        <header className="space-y-4 py-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
            <Rocket className="h-3.5 w-3.5" /> Em andamento
          </div>
          <h1 className="text-3xl font-bold text-foreground sm:text-4xl md:text-5xl">
            B2B — Fisio em Ortopedia para Clínicas
          </h1>
          <p className="mx-auto max-w-2xl px-2 text-base text-muted-foreground sm:text-lg">
            Programa de capacitação e atualização continuada para equipes de fisioterapia
            musculoesquelética. Venda consultiva: LP de captura própria + mini-CRM interno para o
            professor responsável conduzir reuniões e fechar a venda.
          </p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Link
              to="/feoclinicas"
              className="rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold text-primary"
            >
              LP pública /feoclinicas
            </Link>
            <Link
              to="/admin/feoclinicas"
              className="rounded-full border border-border bg-secondary/40 px-4 py-1.5 text-xs font-semibold"
            >
              CRM /admin/feoclinicas
            </Link>
          </div>
        </header>

        <div className="rounded-2xl border border-gold/30 bg-gold/5 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
            <div>
              <h3 className="mb-1 text-base font-bold">Regra de lead: fica só no Lovable</h3>
              <p className="text-sm text-muted-foreground">
                O lead B2B é gravado apenas na tabela <code className="rounded bg-secondary/50 px-1 text-xs">b2b_leads</code>{" "}
                e trabalhado em <strong>/admin/feoclinicas</strong>.{" "}
                <strong>Não vai para RD Station nem RD CRM</strong> e não aparece em /admin/leads.
                O contato é feito pelo WhatsApp para agendar a reunião online; o preço depende do
                número de fisioterapeutas da clínica.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <Briefcase className="h-5 w-5 text-primary" /> Planos na LP · duração {B2B_DURACAO}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {B2B_PLANOS.map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-background/50 p-4">
                <div className="text-sm font-bold">{p.nome}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{p.ideal}</div>
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {p.itens.map((i) => (
                    <li key={i}>• {i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Cada card tem o seu próprio CTA (“Quero o Plano Livre” / “Quero o Plano Guiado”) e usa o
            mesmo formulário de captura — o plano escolhido é gravado no lead só para qualificação.
            CTAs de topo e do rodapé gravam o plano como <strong>Geral</strong>. Preço definido em
            reunião, por número de fisioterapeutas.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <ClipboardList className="h-5 w-5 text-primary" /> Estrutura atual da LP /feoclinicas
          </h3>
          <ol className="space-y-2 text-sm text-muted-foreground">
            {[
              "Hero — “Chega de treinamentos soltos” + mockups do painel da clínica e do app do fisio.",
              "Números — +80 professores, +120 cursos, +2.000h de conteúdo, 12 meses de acesso.",
              "Dores do gestor — 4 cards (dependência do gestor, nível técnico, falta de tempo, escala com qualidade).",
              "Posicionamento — crescer sem perder qualidade; acesso livre vs. acompanhamento personalizado.",
              "Ecossistema FEO — 6 pós-graduações, +120 cursos, Discussões Clínicas Ao Vivo + bloco azul do acompanhamento (diagnóstico, avaliação individual, reavaliações).",
              "Depoimentos — 6 provas sociais em formato WhatsApp (+200 clínicas).",
              "Para quem é — checklist de 5 situações da clínica.",
              "Planos — Plano Livre e Plano Guiado (#planos).",
              "FAQ — 10 perguntas (o que é, diferença entre planos, preço, duração, porte mínimo, avaliação de competências, relatórios, MEC, ao vivo x gravado, como começar) + JSON-LD FAQPage.",
              "CTA final — “Tenho interesse” (botão branco) + rodapé minimalista.",
            ].map((s, i) => (
              <li key={i}>
                {i + 1}. {s}
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-muted-foreground">
            A FAQ é editada em <code className="rounded bg-secondary/50 px-1 text-xs">src/lib/b2b/config.ts</code>{" "}
            (<code className="rounded bg-secondary/50 px-1 text-xs">B2B_FAQ</code>) e é a fonte de
            verdade das objeções usadas também na reunião comercial.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <Users className="h-5 w-5 text-primary" /> Captura e qualificação
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Campos: nome, e-mail e WhatsApp.</li>
            <li>• Pergunta 1: quantos fisioterapeutas tem na clínica? (até 3 / 4 a 10 / mais de 10)</li>
            <li>• Pergunta 2: qual cargo ocupa na clínica? (dono/gestor / fisioterapeuta / outros)</li>
            <li>• Plano selecionado (Livre ou Guiado) gravado junto ao lead.</li>
            <li>• UTMs e página de origem gravadas para atribuição das campanhas do Meta.</li>
            <li>• Tela final: “nossa equipe vai entrar em contato pelo WhatsApp para agendar a reunião”.</li>
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <KanbanSquare className="h-5 w-5 text-primary" /> Mini-CRM /admin/feoclinicas
          </h3>
          <div className="flex flex-wrap gap-2">
            {B2B_STAGES.map((s) => (
              <span
                key={s.id}
                className="rounded-full border px-3 py-1 text-xs font-semibold"
                style={{ borderColor: `${s.color}66`, color: s.color }}
              >
                {s.label}
              </span>
            ))}
          </div>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li>• Acesso com <strong>login separado</strong>: papel <code className="rounded bg-secondary/50 px-1 text-xs">b2b</code> em user_roles (admins também entram). Quem só tem b2b não acessa nenhuma outra área do admin.</li>
            <li>• Kanban arrastável (drag-and-drop) entre as 6 colunas.</li>
            <li>• Card: nome, etiqueta do funil, porte da equipe e botão de WhatsApp com mensagem pronta.</li>
            <li>• Pop-up do card: contato completo, perguntas/respostas, troca de etapa e anotações salvas no banco.</li>
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <ClipboardList className="h-5 w-5 text-primary" /> Etapas do programa (entrega)
          </h3>
          <ol className="space-y-3">
            {B2B_ETAPAS.map((e) => (
              <li key={e.n} className="flex gap-3 rounded-xl border border-border bg-background/50 p-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {e.n}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{e.titulo}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{e.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <Target className="h-5 w-5 text-primary" /> Próximos passos
          </h3>
          <ol className="space-y-3">
            {[
              { t: "Criar o usuário do CRM B2B", d: "Cadastrar o e-mail do professor responsável e atribuir o papel b2b — sem acesso às demais áreas." },
              { t: "Apontar as campanhas do Meta para /feoclinicas", d: "Substituir o formulário de lead nativo pela LP, mantendo UTMs para atribuição." },
              { t: "Rodar teste de ponta a ponta", d: "Captura na LP → card em SEM CONTATO → WhatsApp → movimentação até VENDA/PERDA." },
              { t: "Definir política de preço por faixa", d: "Tabela por número de fisioterapeutas (até 3 / 4 a 10 / mais de 10) para agilizar a reunião comercial." },
              { t: "Trocar depoimentos genéricos por cases nomeados", d: "Os 6 depoimentos da seção DEPOIMENTOS ainda são genéricos — substituir por clínicas reais (nome, cidade e resultado) e, se possível, vídeo." },
              { t: "Alinhar a FAQ com as objeções reais da reunião", d: "Depois das primeiras reuniões, revisar B2B_FAQ com as dúvidas que mais aparecem (preço, porte mínimo, relatórios)." },
            ].map((s, i) => (
              <li key={i} className="flex gap-3 rounded-xl border border-border bg-background/50 p-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{s.t}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <footer className="py-8 text-center text-xs text-muted-foreground">
          /admin/plano-b2b · em andamento
        </footer>
      </div>
    </div>
  );
}
